import type { UserRole } from "@shared/schemas/enums";

// Eigene Datei (ohne Supabase-Import), damit Tests und reine Utilities
// sie ohne Env-Vars laufen lassen koennen.

export function isAdminRole(role: UserRole): boolean {
  return role === "admin" || role === "superadmin";
}

export function isSuperadminRole(role: UserRole): boolean {
  return role === "superadmin";
}

export function isPLRole(role: UserRole): boolean {
  return role === "pl";
}

export function isBuchhaltungRole(role: UserRole): boolean {
  return role === "buchhaltung";
}

// Menschenlesbare Rollen-Bezeichnung fuer die UI (Konto-Menue oben rechts +
// Impersonier-Leiste). Die DB-Werte (pl/buchhaltung/…) sind Kuerzel, keine
// Woerter — hier EINMAL uebersetzt, damit jede Anzeige denselben Text zeigt
// (POL-1 SOFORT). Rein kosmetisch; die Rollen-Logik laeuft weiter ueber die
// Praedikate oben.
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  superadmin: "Superadmin",
  admin: "CEO",
  pl: "Projektleiter",
  buchhaltung: "Buchhaltung",
};

// Strategische Finanz-KPIs (Cash Runway, DSO, Bruttomarge, …) sind fuer
// Superadmin, Admin und Buchhaltung sichtbar — gerendert in der Strategie-
// Sektion von DashboardCEO. Spiegelt das Service-Layer-Gating in
// `loadCashRunway` & co. (CLAUDE.md Prinzip 2 — Defense in Depth).
export function isFinanceRole(role: UserRole): boolean {
  return role === "superadmin" || role === "admin" || role === "buchhaltung";
}

// Projektleitung (Phase 2.9): PL-Bonus, DB pro Projekt und Faktura/Monat
// sind in der Performance-Sektion von DashboardPL sichtbar. Zugang fuer
// pl + admin + superadmin (Sandro per Doppelrolle CEO+PL, Admins koennen
// eigene Projekte fuehren). Buchhaltung bleibt aussen vor — Bonus ist
// PL-Domaene. Spiegelt das Service-Layer-Gating in `plBonusKpi` (Prinzip 2).
export function isProjectLead(role: UserRole): boolean {
  return role === "pl" || role === "admin" || role === "superadmin";
}

// -- Das eine Navigations-Geruest (NAV-1, 2026-09-04) -----------------------
// Vor NAV-1 hatte jede Rolle ihre eigene Liste (ADMIN_NAV, PL_NAV, …) mit je
// eigener Reihenfolge, und die Gruppen-Faltung (POL-2) griff nur fuer admin.
// Folge: Sandro (CEO und PL) fand Verdienst und Produkte in Klappmenues,
// Marvin oben in der Leiste — dieselbe App, zwei Landkarten. Seit NAV-1 gilt:
//
//   EIN Geruest, EINE Reihenfolge (`NAV_SKELETON`). Rollen blenden Blaetter
//   AUS (`VISIBLE_BY_ROLE`), sie verschieben nie. Die Gruppen (`NAV_GROUPS`)
//   gelten fuer alle; ob sie zuklappen, entscheidet die Leistenlaenge der
//   Rolle (`buildTopLevelNav`), kein Rollen-Sonderfall.
//
// Home ist seit 5.1 ein eigenes Nav-Item — fuer alle Rollen, an erster Stelle
// (Daniels Anliegen: bei der Doppelrolle CEO+PL soll die Startseite klar als
// Heimat erkennbar sein). Das Logo bleibt zusaetzlich klickbar
// (`HomeLogoLink`) — Logo fuer «zurueck», Nav-Item fuer den aktiven Bereich.

export type NavItemId =
  | "home"
  | "customers"
  | "projects"
  | "earnings"
  | "invoices"
  | "finanzen"
  | "produkte"
  | "team"
  | "einstellungen"
  | "partner-portal"
  | "aktivitaeten"
  | "leads";

export interface NavItemDefinition {
  readonly id: NavItemId;
  readonly to: string;
  readonly label: string;
  // Eine Zeile, was hinter dem Blatt liegt — die Palette (⌘K, NAV-4) zeigt
  // sie neben dem Titel. Kein zweiter Name, eine Erklaerung; der Titel selbst
  // bleibt das Leisten-Wort (NAV-2).
  readonly description: string;
  readonly end: boolean;
  readonly disabled: boolean;
}

// Home ist die einzige Route mit `end: true` — `NavLink` wuerde `/` sonst auf
// jeder Route als aktiv markieren, weil alle anderen Pfade mit `/` beginnen.
const HOME: NavItemDefinition = {
  id: "home",
  to: "/",
  label: "Home",
  description: "Startseite: Heute, Meldungen und deine Projekte",
  end: true,
  disabled: false,
};
const CUSTOMERS: NavItemDefinition = {
  id: "customers",
  to: "/kunden",
  label: "Kunden",
  description: "Alle Kunden — Suche nach Name, E-Mail, Telefon oder Adresse",
  end: false,
  disabled: false,
};
const PROJECTS: NavItemDefinition = {
  id: "projects",
  to: "/projekte",
  label: "Projekte",
  description: "Eine Liste, fünf Phasen — vom neuen Lead bis zum Abschluss",
  end: false,
  disabled: false,
};
// Verdienst steht seit NAV-1 fuer JEDEN, der es sehen darf, direkt in der
// Leiste — vorher lag es fuer Sandro im «Finanzen»-Dropdown, fuer Marvin oben.
const EARNINGS: NavItemDefinition = {
  id: "earnings",
  to: "/verdienst",
  label: "Verdienst",
  description: "Deckungsbeitrag, Break Even und Bonus je Projekt",
  end: false,
  disabled: false,
};
// Rechnungen-Liste (/rechnungen, InvoicesListPage). UX-3 (2026-06-30): die
// Liste war fertig + RequireFinance-gegated, hatte aber KEINEN Nav-Einstieg —
// Buchhaltung (Jacqueline) kam nicht an die Rechnungen. Eintrag fuer admin +
// buchhaltung (isFinanceRole minus Superadmin-Home-only-Design). Die
// operative Rechnungs-Liste, nicht die KPI-Sicht.
const INVOICES: NavItemDefinition = {
  id: "invoices",
  to: "/rechnungen",
  label: "Rechnungen",
  description: "Rechnungen aus bexio, sortiert nach Fälligkeit",
  end: false,
  disabled: false,
};
// Finanzen-Deep-Dive (/finanzen, FinanzenPage). HOME-6 (2026-06-30): die volle
// strategische Finanzsicht (alle 14 KPIs, CompanyTrendChart, Erfolgsrechnung)
// auf einer eigenen Seite; Sandros Home zeigt nur den Fruehwarn-Streifen. Nur
// admin + buchhaltung (RequireFinance). POL-2 (2026-07-12): Label «Finanzen» →
// «Kennzahlen» — direkt neben «Rechnungen» las sich «Finanzen» zu aehnlich;
// «Kennzahlen» benennt den Inhalt (Strategie-KPIs), nicht die Domaene.
const FINANZEN: NavItemDefinition = {
  id: "finanzen",
  to: "/finanzen",
  label: "Kennzahlen",
  description: "Das Finanz-Cockpit: Kennzahlen aus bexio, Umsatzverlauf, Erfolgsrechnung",
  end: false,
  disabled: false,
};
// Produkte (Baustein ENG-2) — EINE Produktwahrheit: bexio-Artikel + interne
// Produkte, EK/UVP-Pflege, Gruppen-Zuordnung. PS-10: die interne Preis-SICHT
// (EK, Marge, Break-even, UVP) ist fuer ALLE Mitarbeiter. BEDIENUNG (Margen-
// Regler, Inline-Edit, price_mode) bleibt admin-only; Kampfpreis-/Verlustzone
// CEO-only. Das Preisgesetz lebt auf /produkte — NAV-1 verschiebt es nicht.
const PRODUKTE: NavItemDefinition = {
  id: "produkte",
  to: "/produkte",
  label: "Produkte",
  description: "Einkaufspreis, UVP und Marge je Produkt",
  end: false,
  disabled: false,
};
const TEAM: NavItemDefinition = {
  id: "team",
  to: "/team",
  label: "Team",
  description: "Mitarbeiter-Verwaltung — Rollen und Tiers",
  end: false,
  disabled: false,
};
// Die drei Firmen-Dinge aus dem Konto-Menue (NAV-1): Einstellungen und
// Partner Portal (RequireAdmin) und Aktivitaeten (Audit + Interaktionen,
// Oversight-Sicht — Route ungegated, der Service filtert pro Rolle; der
// Einstieg bleibt wie vorher admin-only). Sie gehoeren zur Verwaltung der
// Firma, nicht zur Person — das Konto-Menue traegt seither nur Persoenliches.
const EINSTELLUNGEN: NavItemDefinition = {
  id: "einstellungen",
  to: "/einstellungen",
  label: "Einstellungen",
  description: "OneDrive-Verbindung — Status der Firmen-Integrationen",
  end: false,
  disabled: false,
};
const PARTNER_PORTAL: NavItemDefinition = {
  id: "partner-portal",
  to: "/partner-portal",
  label: "Partner Portal",
  description: "Partner-Anmeldungen prüfen, Rabatt je Partner pflegen",
  end: false,
  disabled: false,
};
const AKTIVITAETEN: NavItemDefinition = {
  id: "aktivitaeten",
  to: "/aktivitaeten",
  label: "Aktivitäten",
  description: "Verlauf der Mutationen und Interaktionen aller Nutzer",
  end: false,
  disabled: false,
};
// Leads (LEAD-23, Marvin 07.10.2026: «Leads als eigenes Menü (hauptmenü)
// weniger vom home»). W12-3 (25.07.2026) hatte den Eintrag gestrichen und den
// Posteingang in den Neu-Reiter von /projekte gelegt — gemessen am 07.10.
// trug die Startseite dafuer 47 von Marvins 58 Zeilen. Der Punkt steht direkt
// hinter Home: die Anfrage kommt vor Kunde und Projekt. Die Liste dahinter
// rendert dieselben Karten mit denselben Knoepfen wie zuvor die Startseite
// (ein Weg), nur am eigenen Ort.
const LEADS: NavItemDefinition = {
  id: "leads",
  to: "/leads",
  label: "Leads",
  description: "Alle offenen Anfragen — was neu ist, was wartet, was als Nächstes zu tun ist",
  end: false,
  disabled: false,
};

// Das Geruest: die eine Reihenfolge fuer alle Rollen. Gruppen-Mitglieder
// stehen zusammenhaengend (Test-gepinnt), damit «an derselben Position flach
// ausgelegt» und «als Dropdown an der Position des ersten Mitglieds» dieselbe
// Stelle meinen.
export const NAV_SKELETON: readonly NavItemDefinition[] = [
  HOME,
  LEADS,
  CUSTOMERS,
  PROJECTS,
  EARNINGS,
  INVOICES,
  FINANZEN,
  PRODUKTE,
  TEAM,
  EINSTELLUNGEN,
  PARTNER_PORTAL,
  AKTIVITAETEN,
];

// Sichtbarkeit pro Rolle — die EINE Wahrheit, welches Blatt wer sieht.
// - superadmin: nur Home; operative Sichten via Impersonation (POL-1), das
//   Zahnrad im Konto-Menue fuehrt auf /system.
// - admin (CEO): alles.
// - Leads (LEAD-23): admin + pl. Die Buchhaltung hat im Posteingang nichts
//   zu suchen (dieselbe Schranke wie `hatLeadsMenue`, shared/leads/leadsMenue).
// - pl: operative Nav + Verdienst + Produkte (PS-10). Kein Team, keine
//   Rechnungen/Kennzahlen (`/finanzen` ist admin/buchhaltung-only, 5.30).
// - buchhaltung: rechnungs-zentriert; Projekte fuer den Rechnungs-Kontext
//   (Session 18h), Produkte-Sicht (PS-10). Kein Verdienst (PL-Domaene).
// Reine Literale (keine Sets, kein map): so bleibt das Geruest fuer das
// API-Bundle tree-shakebar — api-src importiert aus dieser Datei nur
// isAdminRole und soll die Nav nicht mitschleppen.
const VISIBLE_BY_ROLE: Record<UserRole, readonly NavItemId[]> = {
  superadmin: ["home"],
  admin: [
    "home",
    "leads",
    "customers",
    "projects",
    "earnings",
    "invoices",
    "finanzen",
    "produkte",
    "team",
    "einstellungen",
    "partner-portal",
    "aktivitaeten",
  ],
  pl: ["home", "leads", "customers", "projects", "earnings", "produkte"],
  buchhaltung: ["home", "customers", "projects", "invoices", "finanzen", "produkte"],
};

export function navItemsForRole(role: UserRole): readonly NavItemDefinition[] {
  const visible = VISIBLE_BY_ROLE[role];
  return NAV_SKELETON.filter((item) => visible.includes(item.id));
}

// -- Gruppen + Klapp-Regel --------------------------------------------------
// `navItemsForRole` bleibt die EINE Wahrheit ueber die Ziele (flache Liste);
// die Gruppen entscheiden nur, welche Blaetter sich auf der Leiste zu EINEM
// Dropdown zusammenfalten — und die Regel unten, ob ueberhaupt gefaltet wird.
export type NavGroupId = "geld" | "verwaltung";

export interface NavGroupDefinition {
  readonly id: NavGroupId;
  readonly label: string;
  // Die Nav-Item-Ids, die dieses Dropdown sammelt (in Anzeige-Reihenfolge).
  readonly memberIds: readonly NavItemId[];
}

// Rollenuebergreifend (NAV-1): Finanzen = die Geld-Sichten, Verwaltung = die
// Firmen-Dinge. Verdienst gehoert bewusst zu KEINER Gruppe — es steht fuer
// jeden, der es sieht, direkt in der Leiste.
export const NAV_GROUPS: readonly NavGroupDefinition[] = [
  {
    id: "geld",
    label: "Finanzen",
    memberIds: ["invoices", "finanzen"],
  },
  {
    id: "verwaltung",
    label: "Verwaltung",
    memberIds: ["produkte", "team", "einstellungen", "partner-portal", "aktivitaeten"],
  },
];

// Klapp-Regel (NAV-1): Falten ist ein Ueberlauf-Werkzeug (POL-2) und greift
// nur, wo es eng ist. Bis sechs sichtbare Blaetter liegt alles flach — sechs
// Eintraege sind gemessen das, was bei 1280 px neben Suchfeld und Konto-Menue
// Platz hat (Messung im NAV-1-Commit). Sieht eine Rolle mehr, klappen ihre
// Gruppen zu; ein Dropdown braucht dabei mindestens zwei sichtbare
// Mitglieder — ein Ein-Punkt-Dropdown ist ein Umweg ohne Gewinn, das eine
// Mitglied bleibt ein direkter Reiter an derselben Stelle.
//
// Damit sind Jacqueline (6 Blaetter: Rechnungen bleibt EIN Klick — POL-2) und
// Marvin (5) flach wie vor NAV-1, Sandro (11) faltet auf sechs Eintraege.
// Wer einer Rolle ein siebtes Blatt gibt, sieht den Rollen-Test rot — das ist
// der Moment, die Faltung fuer diese Rolle bewusst zu entscheiden.
export const NAV_FLAT_MAX_LEAVES = 6;
export const NAV_GROUP_MIN_MEMBERS = 2;

// Ein Eintrag der obersten Leiste: entweder ein direktes Blatt oder ein
// Sammel-Dropdown mit seinen (fuer die Rolle sichtbaren) Mitgliedern.
export type TopLevelNavEntry =
  | { readonly kind: "leaf"; readonly item: NavItemDefinition }
  | {
      readonly kind: "group";
      readonly group: NavGroupDefinition;
      readonly items: readonly NavItemDefinition[];
    };

// Baut die oberste Leiste aus der flachen Rollen-Nav. Jedes Dropdown erscheint
// an der Position seines ERSTEN sichtbaren Mitglieds — so bleibt die eine
// Reihenfolge des Geruests erhalten, ohne sie separat zu pflegen. Ein Blatt
// ohne Gruppe rendert immer direkt.
export function buildTopLevelNav(
  navItems: readonly NavItemDefinition[],
  groups: readonly NavGroupDefinition[] = NAV_GROUPS,
): readonly TopLevelNavEntry[] {
  const fold = navItems.length > NAV_FLAT_MAX_LEAVES;
  if (!fold) {
    return navItems.map((item) => ({ kind: "leaf" as const, item }));
  }
  const groupByMember = new Map<NavItemId, NavGroupDefinition>();
  for (const group of groups) {
    for (const memberId of group.memberIds) {
      groupByMember.set(memberId, group);
    }
  }
  const emitted = new Set<NavGroupId>();
  const result: TopLevelNavEntry[] = [];
  for (const item of navItems) {
    const group = groupByMember.get(item.id);
    if (!group) {
      result.push({ kind: "leaf", item });
      continue;
    }
    const items = navItems.filter((candidate) =>
      group.memberIds.includes(candidate.id),
    );
    if (items.length < NAV_GROUP_MIN_MEMBERS) {
      result.push({ kind: "leaf", item });
      continue;
    }
    if (emitted.has(group.id)) continue;
    emitted.add(group.id);
    result.push({ kind: "group", group, items });
  }
  return result;
}

// Die Leiste einer Rolle — Desktop-Leiste und Mobile-Sheet lesen beide hier.
export function topLevelNavForRole(role: UserRole): readonly TopLevelNavEntry[] {
  return buildTopLevelNav(navItemsForRole(role), NAV_GROUPS);
}
